import React, { useState } from 'react';
import { Itinerary } from '../types';
import { updateItinerary, deleteItinerary } from '../services';
import './EventEditModal.css'; // Reuse the same modal styles

interface ItineraryEditModalProps {
  itinerary: Itinerary;
  onClose: () => void;
  onSave: (updated: Itinerary) => void;
  onDelete: () => void;
}

const TYPE_OPTIONS = [
  { value: 'day_trip', label: 'Day Trip' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'playdate', label: 'Playdate' },
  { value: 'activity', label: 'Activity' },
  { value: 'other', label: 'Other' },
];

const ItineraryEditModal: React.FC<ItineraryEditModalProps> = ({
  itinerary,
  onClose,
  onSave,
  onDelete,
}) => {
  const [title, setTitle] = useState(itinerary.title);
  const [description, setDescription] = useState(itinerary.description || '');
  const [type, setType] = useState(itinerary.type);
  const [startDate, setStartDate] = useState(itinerary.start_date || '');
  const [endDate, setEndDate] = useState(itinerary.end_date || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!startDate) {
      setError('Start date is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const updated = await updateItinerary(itinerary.id, {
        title: title.trim(),
        description: description.trim(),
        type,
        start_date: startDate,
        end_date: endDate || startDate,
      }, itinerary.content_revision ?? 0);
      onSave(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${itinerary.title}"? This cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await deleteItinerary(itinerary.id);
      onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setIsDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Trip</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-body">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Trip name"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                className="form-input"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-delete"
            onClick={handleDelete}
            disabled={isDeleting || isSaving}
          >
            {isDeleting ? 'Deleting...' : 'Delete Trip'}
          </button>
          <div className="footer-right">
            <button className="btn-cancel" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={isSaving || isDeleting}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryEditModal;
